import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AlertCircle, CheckCircle2, Clock, FileAudio, Loader2 } from 'lucide-react';
import { Logo } from '../components/Logo';
import { Button, Card } from '../components/UIComponents';
import { handleApiError } from '../api/client';
import {
  confirmPublicUploadLinkUpload,
  getPublicUploadLinkStatus,
  initiatePublicUploadLinkUpload,
  type UploadLinkConfirmResponse,
} from '../api/uploadLinks';
import MediaDropzone from '../components/MediaDropzone';
import { useTranslation } from 'react-i18next';

type PageState = 'loading' | 'ready' | 'not_found' | 'expired' | 'used' | 'success';

function guessMimeType(file: File): string {
  if (file.type) return file.type;
  const name = file.name.toLowerCase();
  if (name.endsWith('.mp3')) return 'audio/mpeg';
  if (name.endsWith('.m4a')) return 'audio/mp4';
  if (name.endsWith('.wav')) return 'audio/wav';
  if (name.endsWith('.webm')) return 'audio/webm';
  if (name.endsWith('.ogg')) return 'audio/ogg';
  if (name.endsWith('.flac')) return 'audio/flac';
  if (name.endsWith('.aac')) return 'audio/aac';
  return '';
}

function isVideoFile(file: File): boolean {
  if (file.type?.startsWith('video/')) return true;
  const name = file.name.toLowerCase();
  return name.endsWith('.mp4') || name.endsWith('.mov') || name.endsWith('.mkv') || name.endsWith('.webm');
}

function formatTimeLeft(expiresAt: string): string {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (!Number.isFinite(ms)) return '';
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatBytes(bytes: number) {
  const kb = 1024;
  const mb = kb * 1024;
  const gb = mb * 1024;
  if (bytes >= gb) return `${(bytes / gb).toFixed(2)} GB`;
  if (bytes >= mb) return `${(bytes / mb).toFixed(2)} MB`;
  if (bytes >= kb) return `${(bytes / kb).toFixed(2)} KB`;
  return `${bytes} B`;
}

const PhoneUploadPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();

  const [state, setState] = useState<PageState>('loading');
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<string>('');
  const [result, setResult] = useState<UploadLinkConfirmResponse | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const timeLeft = useMemo(() => (expiresAt ? formatTimeLeft(expiresAt) : ''), [expiresAt]);

  const loadStatus = useCallback(async () => {
    if (!token) return;
    setState('loading');
    setError('');
    try {
      const status = await getPublicUploadLinkStatus(token);
      setExpiresAt(status.expires_at);
      setState('ready');
    } catch (err: unknown) {
      const axiosError = err as { response?: { status?: number; data?: { error?: { code?: string } } } };
      const httpStatus = axiosError.response?.status;
      if (httpStatus === 404) {
        setState('not_found');
        return;
      }
      if (httpStatus === 410) {
        const code = axiosError.response?.data?.error?.code;
        if (code === 'EXPIRED') setState('expired');
        else if (code === 'USED') setState('used');
        else setState('expired');
        return;
      }
      setError(handleApiError(err));
      setState('ready');
    }
  }, [token]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const uploadToPresignedUrl = useCallback(async (url: string, fileToUpload: File, contentType: string) => {
    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url, true);
      if (contentType) {
        xhr.setRequestHeader('Content-Type', contentType);
      }

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0) {
          setProgress(Math.min(99, Math.round((event.loaded / event.total) * 100)));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setProgress(100);
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(fileToUpload);
    });
  }, []);

  const startUpload = useCallback(async () => {
    if (!token || !file) return;
    setError('');
    setProgress(0);
    setIsUploading(true);

    const contentType = guessMimeType(file);
      if (!contentType) {
        setError(t('upload.phoneUpload.unsupportedAudio'));
      setIsUploading(false);
      return;
    }

    try {
      const init = await initiatePublicUploadLinkUpload(token, {
        file_name: file.name || 'recording',
        file_size: file.size,
        content_type: contentType,
      });

      await uploadToPresignedUrl(init.upload_url, file, contentType);

      const confirm = await confirmPublicUploadLinkUpload(token);
      setResult(confirm);
      setState('success');
    } catch (err) {
      setError(handleApiError(err));
      setIsUploading(false);
      setState('ready');
    }
  }, [file, token, uploadToPresignedUrl]);

  return (
    <>
      <Helmet>
        <title>{t('upload.phoneUpload.pageTitle')}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="max-w-lg mx-auto">
        <div className="flex items-center justify-between px-2 py-3">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-900 dark:text-zinc-100"
             aria-label={t('upload.phoneUpload.goToOmniScribe')}
          >
            <Logo className="h-8 w-8 text-indigo-600 dark:text-indigo-400" />
            <span className="font-bold text-lg">OmniScribe</span>
          </button>
          {timeLeft && state === 'ready' && (
            <div className="text-xs text-slate-500 dark:text-zinc-400 flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              <span>{timeLeft}</span>
            </div>
          )}
        </div>

        {state === 'loading' && (
          <Card className="p-6 flex items-center justify-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
            <span className="text-slate-600 dark:text-zinc-400">{t('upload.phoneUpload.loadingLink')}</span>
          </Card>
        )}

        {state === 'not_found' && (
          <Card className="p-6 text-center space-y-3">
            <AlertCircle className="h-10 w-10 text-slate-400 mx-auto" />
            <div>
              <p className="font-semibold text-slate-900 dark:text-zinc-100">{t('upload.phoneUpload.linkNotFound')}</p>
              <p className="text-sm text-slate-600 dark:text-zinc-400 mt-1">{t('upload.phoneUpload.linkNotFoundHelp')}</p>
            </div>
            <Button onClick={() => navigate('/')}>{t('upload.phoneUpload.goHome')}</Button>
          </Card>
        )}

        {state === 'expired' && (
          <Card className="p-6 text-center space-y-3">
            <Clock className="h-10 w-10 text-amber-500 mx-auto" />
            <div>
              <p className="font-semibold text-slate-900 dark:text-zinc-100">{t('upload.phoneUpload.linkExpired')}</p>
              <p className="text-sm text-slate-600 dark:text-zinc-400 mt-1">{t('upload.phoneUpload.linkExpiredHelp')}</p>
            </div>
            <Button onClick={() => navigate('/')}>{t('upload.phoneUpload.goHome')}</Button>
          </Card>
        )}

        {state === 'used' && (
          <Card className="p-6 text-center space-y-3">
            <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto" />
            <div>
              <p className="font-semibold text-slate-900 dark:text-zinc-100">{t('upload.phoneUpload.linkUsed')}</p>
              <p className="text-sm text-slate-600 dark:text-zinc-400 mt-1">{t('upload.phoneUpload.linkUsedHelp')}</p>
            </div>
            <Button onClick={() => navigate('/')}>{t('upload.phoneUpload.goHome')}</Button>
          </Card>
        )}

        {state === 'ready' && (
          <Card className="p-6 space-y-5">
            <div className="space-y-1">
              <p className="text-lg font-semibold text-slate-900 dark:text-zinc-100">{t('upload.phoneUpload.uploadTitle')}</p>
              <p className="text-sm text-slate-600 dark:text-zinc-400">
                {t('upload.phoneUpload.uploadHelp')}
              </p>
            </div>

            {!file ? (
              <MediaDropzone
                embedded={true}
                accept="audio/*"
                title={t('upload.phoneUpload.tapToSelect')}
                description={(
                  <>
                    {t('upload.phoneUpload.dropzoneDescription').split('\n').map((line, idx) => (
                      <React.Fragment key={idx}>
                        {idx > 0 && <br />}
                        {line}
                      </React.Fragment>
                    ))}
                  </>
                )}
                multiple={false}
                onFileSelected={(selectedFile) => {
                  if (isVideoFile(selectedFile)) {
                      setError(t('upload.phoneUpload.audioOnly'));
                    return;
                  }
                  setFile(selectedFile);
                  setError('');
                  setProgress(0);
                }}
              />
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 dark:border-zinc-800 bg-slate-50 dark:bg-zinc-900 p-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-xl bg-indigo-600/10 flex items-center justify-center border border-indigo-200/50 dark:border-indigo-500/20 dark:bg-indigo-500/10 shrink-0">
                      <FileAudio className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 dark:text-zinc-100 truncate">{file.name}</p>
                      <p className="text-xs text-slate-500 dark:text-zinc-400">{formatBytes(file.size)}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={isUploading}
                    onClick={() => {
                      setFile(null);
                      setError('');
                      setProgress(0);
                    }}
                  >
                    {t('upload.phoneUpload.change')}
                  </Button>
                </div>
              </div>
            )}

            {progress > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs text-slate-500 dark:text-zinc-400">
                  <span>{t('upload.phoneUpload.uploading')}</span>
                  <span>{progress}%</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 dark:bg-zinc-800 overflow-hidden">
                  <div className="h-full bg-indigo-600 transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}

            {error && (
              <div className="flex items-start gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
                <div className="text-sm text-red-700 dark:text-red-300">{error}</div>
              </div>
            )}

            <Button
              onClick={startUpload}
              disabled={!file}
              className="w-full"
              isLoading={isUploading}
            >
              {t('upload.phoneUpload.uploadAndStart')}
            </Button>
          </Card>
        )}

        {state === 'success' && (
          <Card className="p-6 text-center space-y-3">
            <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto" />
            <div>
              <p className="font-semibold text-slate-900 dark:text-zinc-100">{t('upload.phoneUpload.uploadComplete')}</p>
              <p className="text-sm text-slate-600 dark:text-zinc-400 mt-1">
                {t('upload.phoneUpload.successHelp')}
              </p>
              {result?.transcription_id && (
                <p className="text-xs text-slate-500 dark:text-zinc-500 mt-2">
                  {t('upload.phoneUpload.jobId')} {result.transcription_id}
                </p>
              )}
            </div>
            <Button onClick={() => navigate('/')}>{t('upload.phoneUpload.openOmniScribe')}</Button>
          </Card>
        )}
      </div>
    </>
  );
};

export default PhoneUploadPage;
